import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';

import SchedulerV2Context from '.';

import {
  type ViewMode,
  type FilterState,
  DEFAULT_FILTER_STATE,
  type ResourceV2,
} from '@/components/SchedulerComponents/types';

const STORAGE_KEYS = {
  viewMode: 'scheduler_v2_view_mode',
  filters: 'scheduler_v2_filters',
};

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

const VALID_VIEW_MODES: ViewMode[] = ['day', 'week', 'month'];

interface SchedulerV2ProviderProps {
  children: React.ReactNode;
}

const SchedulerV2Provider = ({ children }: SchedulerV2ProviderProps) => {
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    const stored = readStorage<string>(STORAGE_KEYS.viewMode, 'week');
    return VALID_VIEW_MODES.includes(stored as ViewMode) ? (stored as ViewMode) : 'week';
  });

  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());

  const [filters, setFiltersState] = useState<FilterState>(() => {
    const VALID_STATUSES = ['not_started', 'in_progress', 'on_hold'];
    const stored = readStorage<Partial<FilterState>>(STORAGE_KEYS.filters, {});
    return {
      ...DEFAULT_FILTER_STATE,
      ...stored,
      status: (stored.status ?? []).filter((s) => VALID_STATUSES.includes(s)),
    };
  });

  const [filterBarOpen, setFilterBarOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<ResourceV2 | null>(null);

  const setViewMode = (v: ViewMode) => {
    setViewModeState(v);
    localStorage.setItem(STORAGE_KEYS.viewMode, JSON.stringify(v));
  };

  const setFilters = (f: FilterState) => {
    setFiltersState(f);
    localStorage.setItem(STORAGE_KEYS.filters, JSON.stringify(f));
  };

  const activeFilterCount = [
    filters.status.length > 0,
    filters.projectIds.length > 0,
    filters.tagIds.length > 0,
    filters.searchBy.trim().length > 0,
    filters.clientIds.length > 0,
    filters.city !== null && filters.city !== '',
    filters.zip !== null && filters.zip !== '',
    filters.placeholderOnly,
    filters.issueIds.length > 0,
    filters.issueSearch.trim().length > 0,
    filters.eventSearch.trim().length > 0,
  ].filter(Boolean).length;

  return (
    <SchedulerV2Context.Provider
      value={{
        viewMode,
        setViewMode,
        currentDate,
        setCurrentDate,
        filters,
        setFilters,
        filterBarOpen,
        setFilterBarOpen,
        activeFilterCount,
        selectedResource,
        setSelectedResource,
      }}
    >
      {children}
    </SchedulerV2Context.Provider>
  );
};

export default SchedulerV2Provider;
